import React from 'react';
import { BaseStep, useFormData, BaseStepProps } from '../BaseStep';
import FormSection from '../../../FormSection';
import { Toggle } from '../Toggle';
import ownershipIcon from '../../../../assets/icons/ownership.png';
import tractionIcon from '../../../../assets/icons/traction.png';
import { ownershipSelectOptions, tractionSelectOptions } from '../../../../constants/ownership';
import { useProfile } from '../../../../contexts/ProfileContext';

const OwnershipStep: React.FC<BaseStepProps> = ({
  userType,
  onCreateProfile,
  hasProfile,
  onComplete,
  shouldValidate,
  onValidationHandled,
  onValidationResult,
}) => {
  const { investorProfile } = useProfile();

  // Filter out placeholder options
  const capitalizationOptions = ownershipSelectOptions.filter(opt => opt.value !== '');
  const tractionOptions = tractionSelectOptions.filter(opt => opt.value !== '');

  // Initialize selected values from profile
  const [selectedCapitalization, setSelectedCapitalization] = React.useState<string[]>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (investorProfile as any)?.capitalization || []
  );
  const [selectedTraction, setSelectedTraction] = React.useState<string[]>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (investorProfile as any)?.traction || []
  );

  const { formData, setFormData } = useFormData({});

  // Handle capitalization toggle
  const handleCapitalizationToggle = (value: string) => {
    setSelectedCapitalization(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      }
      return [...prev, value];
    });
  };

  // Handle traction toggle
  const handleTractionToggle = (value: string) => {
    setSelectedTraction(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      }
      return [...prev, value];
    });
  };

  // Update formData when selections change
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      capitalization: selectedCapitalization,
      traction: selectedTraction,
    }));
  }, [selectedCapitalization, selectedTraction, setFormData]);

  // Listen for validation requests from parent
  // Nothing is required in this step, so validation always passes
  React.useEffect(() => {
    if (shouldValidate) {
      // Always valid - nothing is required
      const isValid = true;

      // Notify parent about validation result
      if (onValidationResult) {
        onValidationResult(isValid);
      }

      // Notify parent that validation was handled
      if (onValidationHandled) {
        onValidationHandled();
      }
    }
  }, [shouldValidate, onValidationHandled, onValidationResult]);

  // Expose form data to parent component
  React.useEffect(() => {
    if (onCreateProfile) {
      onCreateProfile(formData);
    }
  }, [formData, onCreateProfile]);

  return (
    <BaseStep
      userType={userType}
      onCreateProfile={onCreateProfile}
      hasProfile={hasProfile}
      onComplete={onComplete}
    >
      <FormSection icon={ownershipIcon} title="CAPITALIZATION">
        <div className="space-y-3">
          {capitalizationOptions.map(option => (
            <Toggle
              key={option.value}
              name={option.value}
              label={option.name}
              value={selectedCapitalization.includes(option.value)}
              onChange={() => handleCapitalizationToggle(option.value)}
            />
          ))}
        </div>
      </FormSection>

      <FormSection icon={tractionIcon} title="TRACTION">
        <div className="space-y-2 sm:space-y-3">
          {tractionOptions.map(option => (
            <Toggle
              key={option.value}
              name={option.value}
              label={option.name}
              value={selectedTraction.includes(option.value)}
              onChange={() => handleTractionToggle(option.value)}
            />
          ))}
        </div>
      </FormSection>
    </BaseStep>
  );
};

export default OwnershipStep;
